import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/index.css';
import App from './App';
import { ConvexReactClient } from 'convex/react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { GameProvider } from './contexts/GameContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { initAnalytics, trackError } from './services/analytics';
import { initSentry } from './sentry';
import * as Sentry from '@sentry/react';

// Initialize Sentry error tracking (FIR-358) - must run before React renders
initSentry();

// Initialize PostHog analytics (FIR-85)
initAnalytics();

window.addEventListener('error', (e) => {
  console.error('Runtime error:', e.message);
  Sentry.captureException(e.error || new Error(e.message));
  trackError('runtime', e.message, { stack: e.error?.stack });
});

// Always use the environment variable for Convex URL
const convex = new ConvexReactClient(
  process.env.REACT_APP_CONVEX_URL as string
);

const clerkPublishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

if (!clerkPublishableKey) {
  const errorMessage =
    'Missing REACT_APP_CLERK_PUBLISHABLE_KEY. Update your .env or .env.local with a Clerk publishable key.';
  console.error(errorMessage);
  Sentry.captureMessage(errorMessage, 'error');
}

function MissingClerkKeyNotice() {
  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(circle at top, rgba(99,102,241,0.15), rgba(15,23,42,1))',
        color: 'var(--text-secondary, #f1f5f9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px',
        textAlign: 'center',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <h1 style={{ fontSize: '32px', marginBottom: '12px' }}>
        Clerk publishable key not configured
      </h1>
      <p style={{ maxWidth: '520px', lineHeight: 1.6, marginBottom: '24px' }}>
        Set <code>REACT_APP_CLERK_PUBLISHABLE_KEY</code> in your environment
        file (for example <code>.env</code> or <code>.env.local</code>). You can
        obtain a key from the Clerk dashboard under <strong>API Keys</strong>.
      </p>
      <p style={{ fontSize: '14px', color: 'var(--text-muted, #94a3b8)' }}>
        After updating the key, restart the dev server so the new environment
        variable is picked up.
      </p>
    </div>
  );
}

function Root() {
  if (!clerkPublishableKey) {
    return <MissingClerkKeyNotice />;
  }

  return (
    <HelmetProvider>
      <ClerkProvider publishableKey={clerkPublishableKey}>
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          <AuthProvider>
            <ThemeProvider>
              <GameProvider>
                <MessagingProvider>
                  <App />
                </MessagingProvider>
              </GameProvider>
            </ThemeProvider>
          </AuthProvider>
        </ConvexProviderWithClerk>
      </ClerkProvider>
    </HelmetProvider>
  );
}
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);
